import { type FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { legalPolicySuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import type { LegalPolicyTemplateListItem } from './types';
import { LEGAL_POLICY_TYPE_OPTIONS } from './legal-policy-type-labels';

const POLICY_TYPES = LEGAL_POLICY_TYPE_OPTIONS;
const PER_PAGE = 10;

const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';
function templateOriginLabel(template: LegalPolicyTemplateListItem): string {
  const parts: string[] = [];
  if (template.requiredAtContractSigning) parts.push('Firma contrato');
  if (template.requiredAtRegister) parts.push('Registro');
  return parts.join(', ') || '—';
}

function initials(title: string): string {
  return title.trim().charAt(0).toUpperCase() || '?';
}

export function LegalPoliciesPage() {
  const { hasPermission } = usePermissions();
  const canAdminister = hasPermission(PERMISSIONS.POLITICAS_LEGALES_ADMINISTRAR);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState('data_treatment');
  const [deleteTarget, setDeleteTarget] = useState<LegalPolicyTemplateListItem | null>(null);
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['legal-policies', 'templates'],
    queryFn: () => api<{ data: LegalPolicyTemplateListItem[] }>('/legal/policies/templates'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: {
      title: string;
      slug: string;
      type: string;
      consentLabel: string;
      linkLabel: string;
    }) => api<{ id: string }>('/legal/policies/templates', { method: 'POST', body: payload }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['legal-policies', 'templates'] });
      setModalOpen(false);
      setTitle('');
      setSlug('');
      toast(legalPolicySuccessToast('create'));
      navigate(`/politicas-legales/${created.id}/editor`);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo crear la política legal'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/legal/policies/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legal-policies', 'templates'] });
      setDeleteTarget(null);
      toast(legalPolicySuccessToast('delete'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la política legal'));
    },
  });

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedSlug = slug.trim().toLowerCase();
    if (!trimmedTitle || !trimmedSlug) return;
    createMutation.mutate({
      title: trimmedTitle,
      slug: trimmedSlug,
      type,
      consentLabel: `Acepto la política «${trimmedTitle}».`,
      linkLabel: 'Ver política completa',
    });
  }

  const allTemplates = data?.data ?? [];
  const total = allTemplates.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const firstItem = total === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, total);

  const templates = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return allTemplates.slice(start, start + PER_PAGE);
  }, [allTemplates, currentPage]);

  return (
    <div className="lms-legal-policies-admin lms-site-admin-hub-page font-sans">
      <header className="lms-legal-policies-admin__page-header mb-6">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=plugins" label="Volver a Administración" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Políticas legales</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Plantillas de consentimiento legal (tratamiento de datos, etc.) para registro y firma de contratos.
            </p>
          </div>
        </div>
        <div className="lms-legal-policies-admin__actions">
          <Link to="/politicas-legales/aceptaciones" className="lms-legal-policies-admin__btn-outline">
            Ver aceptaciones
          </Link>
          {canAdminister && (
          <button
            type="button"
            className="lms-legal-policies-admin__btn-primary"
            onClick={() => setModalOpen(true)}
          >
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
              add
            </span>
            Nueva plantilla
          </button>
          )}
        </div>
      </header>

      {error ? <p className="text-sm text-red-600">No se pudieron cargar las plantillas.</p> : null}

      {!error ? (
        <div className="lms-gradebook lms-gradebook--forum">
          <div className="lms-course-forum-card">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader label="Cargando plantillas…" />
              </div>
            ) : total === 0 ? (
              <div className="lms-gradebook__table-wrap">
                <div className="lms-gradebook__empty">
                  <div className="lms-gradebook__empty-icon" aria-hidden="true">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      policy
                    </span>
                  </div>
                  <h3>Sin plantillas</h3>
                  <p>Aún no hay plantillas de políticas legales.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th className="lms-gradebook__th" scope="col">
                          Plantilla
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Estado
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Slug
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Versión
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Origen
                        </th>
                        <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((template) => (
                        <tr key={template.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              <div className="lms-gradebook__avatar" aria-hidden="true">
                                {initials(template.title)}
                              </div>
                              <div className="min-w-0">
                                {canAdminister ? (
                                  <Link
                                    to={`/politicas-legales/${template.id}/editor`}
                                    className="lms-gradebook__name"
                                  >
                                    {template.title}
                                  </Link>
                                ) : (
                                  <span className="lms-gradebook__name">{template.title}</span>
                                )}
                              </div>
                            </div>
                          </th>
                          <td className="lms-gradebook__td">
                            <Badge variant={template.isActive ? 'success' : 'default'}>
                              {template.isActive ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </td>
                          <td className="lms-gradebook__td">{template.slug}</td>
                          <td className="lms-gradebook__td">v{template.version}</td>
                          <td className="lms-gradebook__td">{templateOriginLabel(template)}</td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            {canAdminister ? (
                              <div className="flex items-center justify-end gap-1">
                                <Link
                                  to={`/politicas-legales/${template.id}/editor`}
                                  className="lms-gradebook__action"
                                  aria-label={`Editar ${template.title}`}
                                  title="Editar"
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    edit
                                  </span>
                                </Link>
                                <button
                                  type="button"
                                  className="lms-gradebook__action"
                                  aria-label={`Eliminar ${template.title}`}
                                  title="Eliminar"
                                  onClick={() => setDeleteTarget(template)}
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    delete
                                  </span>
                                </button>
                              </div>
                            ) : (
                              <span className="lms-gradebook__muted">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="lms-course-forum-card__footer">
                  <span className="lms-course-forum-card__meta">
                    Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de <strong>{total}</strong>{' '}
                    resultados
                  </span>
                  <div className="lms-course-forum-card__pager">
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página anterior"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_left
                      </span>
                    </button>
                    <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página siguiente"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_right
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {modalOpen ? (
        <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 sm:p-6">
          <button
            type="button"
            className="absolute inset-0 bg-dash-on-surface/50"
            aria-label="Cerrar"
            onClick={() => setModalOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-legal-policy-title"
            className="relative z-10 w-full max-w-lg overflow-hidden rounded-xl border border-dash-outline-variant bg-white shadow-lg"
          >
            <form onSubmit={handleCreate}>
              <div className="bg-white p-6">
                <div className="mb-6 flex items-center gap-2">
                  <span className="material-symbols-outlined text-dash-primary" aria-hidden="true">
                    policy
                  </span>
                  <h2 id="new-legal-policy-title" className="text-xl font-semibold text-dash-on-surface">
                    Nueva política legal
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="legal-policy-title" className={LABEL}>
                      Título <span className="text-error-600">*</span>
                    </label>
                    <input
                      id="legal-policy-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Política de privacidad"
                      required
                      className={FIELD}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="legal-policy-slug" className={LABEL}>
                      Slug (URL pública) <span className="text-error-600">*</span>
                    </label>
                    <input
                      id="legal-policy-slug"
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="politica-privacidad"
                      required
                      className={FIELD}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="legal-policy-type" className={LABEL}>
                      Tipo <span className="text-error-600">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="legal-policy-type"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        required
                        className={`${FIELD} cursor-pointer appearance-none pr-10`}
                      >
                        {POLICY_TYPES.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                      <span
                        className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant"
                        aria-hidden="true"
                      >
                        <span className="material-symbols-outlined">expand_more</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <footer className="flex flex-col justify-end gap-4 border-t border-dash-outline-variant bg-secondary-50 p-6 sm:flex-row">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-8 py-3 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-100"
                  onClick={() => setModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="inline-flex items-center justify-center rounded-lg bg-dash-primary px-8 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
                >
                  {createMutation.isPending ? 'Creando…' : 'Crear'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Eliminar plantilla"
        description={`¿Eliminar «${deleteTarget?.title}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
